import React from "react";
import Navbar from "../components/Navbar.jsx";
import Footer from "../components/Footer.jsx";

const TermsOfService = () => {
  return (
    <div>
      <Navbar />

      <main className="max-w-5xl mx-auto px-6 py-20 space-y-16">
        {/* Header */}
        <header className="space-y-2">
          <h1 className="text-4xl font-bold">
            AgoraVoiceAI - Terms of Service
          </h1>
          <p className="text-gray-500 text-sm">
            Last Updated: December 5, 2025
          </p>
        </header>

        {/* Body */}
        <section className="space-y-10">
          <p>
            Welcome to AgoraVoiceAI. These Terms of Service (“Terms”) govern
            your access to and use of the AgoraVoiceAI platform, website, and
            services (“Service”), provided by Palladian Technologies LTD
            (“AgoraVoiceAI”, “we”, “us”, or “our”). By using the Service, you
            agree to these Terms.
          </p>

          {/* Section 1 */}
          <div>
            <h2 className="text-2xl font-semibold border-b pb-2">
              1. Description of Service
            </h2>
            <p className="mt-2">
              AgoraVoiceAI provides tools for businesses to build, deploy, and
              manage AI-powered voice agents for customer interaction and
              workflow automation.
            </p>
          </div>

          {/* Section 2 */}
          <div>
            <h2 className="text-2xl font-semibold border-b pb-2">
              2. User Accounts
            </h2>

            <h3 className="text-lg font-medium mt-4">2.1. Eligibility</h3>
            <p>You must be at least 18 years old to use the Service.</p>

            <h3 className="text-lg font-medium mt-4">
              2.2. Account Registration
            </h3>
            <p>
              You must create an account and provide accurate and complete
              information. Paid plans require valid payment information.
            </p>

            <h3 className="text-lg font-medium mt-4">2.3. Account Security</h3>
            <p>
              You are responsible for your account activity and must notify us
              immediately of unauthorized use.
            </p>
          </div>

          {/* Section 3 */}
          <div>
            <h2 className="text-2xl font-semibold border-b pb-2">
              3. Intellectual Property Rights
            </h2>

            <h3 className="text-lg font-medium mt-4">
              3.1. AgoraVoiceAI Ownership
            </h3>
            <p>
              We retain all rights to our platform, code, branding, and
              underlying technology.
            </p>

            <h3 className="text-lg font-medium mt-4">
              3.2. User Ownership & License
            </h3>
            <p>
              You own your custom configurations, scripts, and inputs ("User
              Content").
            </p>
            <p>
              Generated outputs (audio, transcripts, responses) are jointly
              owned by you and AgoraVoiceAI with non-exclusive worldwide usage
              rights for both parties.
            </p>
            <p>
              You grant AgoraVoiceAI a license to use your User Content solely
              to operate and improve the Service.
            </p>

            <h3 className="text-lg font-medium mt-4">3.3. Customer Data</h3>
            <p>
              You are responsible for compliance with privacy laws regarding
              Customer Data. You grant AgoraVoiceAI the right to use anonymized
              and aggregated Customer Data to improve the Service.
            </p>
          </div>

          {/* Section 4 */}
          <div>
            <h2 className="text-2xl font-semibold border-b pb-2">
              4. Acceptable Use Policy
            </h2>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>No illegal activities (fraud, scams, harassment).</li>
              <li>No impersonation or deceptive identity use.</li>
              <li>
                No reverse engineering or attempting to access source code.
              </li>
              <li>No spam, robocalling, or unlawful communications.</li>
              <li>No violations of privacy laws or recording laws.</li>
            </ul>
          </div>

          {/* Section 5 */}
          <div>
            <h2 className="text-2xl font-semibold border-b pb-2">
              5. Fees, Payments & Cancellation
            </h2>

            <h3 className="text-lg font-medium mt-4">5.1. Subscriptions</h3>
            <p>Billed monthly in advance.</p>

            <h3 className="text-lg font-medium mt-4">5.2. Payment</h3>
            <p>We use Stripe for payment processing.</p>

            <h3 className="text-lg font-medium mt-4">5.3. Price Changes</h3>
            <p>We may update pricing with prior notice.</p>

            <h3 className="text-lg font-medium mt-4">5.4. Cancellation</h3>
            <p>
              You may cancel anytime; access continues until the billing period
              ends.
            </p>

            <h3 className="text-lg font-medium mt-4">5.5. Refunds</h3>
            <p>Payments are non-refundable except at our discretion.</p>
          </div>

          {/* Section 6 */}
          <div>
            <h2 className="text-2xl font-semibold border-b pb-2">
              6. Termination
            </h2>
            <p className="mt-2">
              We may suspend or terminate your account for violations of these
              Terms or for any reason at our discretion.
            </p>
          </div>

          {/* Section 7 */}
          <div>
            <h2 className="text-2xl font-semibold border-b pb-2">
              7. Disclaimers & Liability
            </h2>

            <h3 className="text-lg font-medium mt-4">
              7.1. Disclaimer of Warranties
            </h3>
            <p>The Service is provided “as is” without warranties.</p>

            <h3 className="text-lg font-medium mt-4">7.2. AI Disclaimer</h3>
            <p>
              AI-generated outputs may be inaccurate or offensive. You are
              responsible for evaluating results before use.
            </p>

            <h3 className="text-lg font-medium mt-4">
              7.3. Limitation of Liability
            </h3>
            <p>
              Our liability is limited to the total fees you paid in the last 12
              months. We are not liable for indirect or consequential damages.
            </p>
          </div>

          {/* Section 8 */}
          <div>
            <h2 className="text-2xl font-semibold border-b pb-2">
              8. Indemnification
            </h2>
            <p className="mt-2">
              You agree to indemnify and hold us harmless for claims arising
              from your use of the Service, User Content, Customer Data, or
              violations of law.
            </p>
          </div>

          {/* Section 9 */}
          <div>
            <h2 className="text-2xl font-semibold border-b pb-2">
              9. Governing Law & Dispute Resolution
            </h2>

            <h3 className="text-lg font-medium mt-4">9.1. Governing Law</h3>
            <p>Illinois state law governs these Terms.</p>

            <h3 className="text-lg font-medium mt-4">
              9.2. Dispute Resolution
            </h3>
            <p>
              Disputes will first be handled informally. If unresolved,
              arbitration under the American Arbitration Association applies.
            </p>
          </div>

          {/* Section 10 */}
          <div>
            <h2 className="text-2xl font-semibold border-b pb-2">
              10. General Terms
            </h2>

            <h3 className="text-lg font-medium mt-4">10.1. Changes to Terms</h3>
            <p>We may update these Terms; continued use means acceptance.</p>

            <h3 className="text-lg font-medium mt-4">10.2. Contact</h3>
            <p>
              For questions, contact us at: <br />
              <strong>info@agoravoice.ai</strong>
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default TermsOfService;
